import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, LayoutGrid, HelpCircle, Medal, Crown, Star, RefreshCw } from 'lucide-react';
import api from '../../api';

const SCOPES = [
  { key: 'class', label: 'My Class' },
  { key: 'school', label: 'My School' },
  { key: 'global', label: 'EDUVA-Wide' },
];

const MEDAL = ['🥇', '🥈', '🥉'];

function RankBadge({ rank }) {
  if (rank <= 3) return <span className="text-lg">{MEDAL[rank - 1]}</span>;
  return <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">{rank}</span>;
}

export default function LeaderboardPage() {
  const [tab, setTab] = useState('quiz');   // quiz | games
  const [scope, setScope] = useState('school');
  const [quizBoard, setQuizBoard] = useState([]);
  const [gamesBoard, setGamesBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizSubject, setQuizSubject] = useState('');
  const [subjects, setSubjects] = useState([]);

  const loadQuiz = useCallback(async (s, subj) => {
    try {
      const url = `/quiz/leaderboard?scope=${s}${subj ? `&subject=${encodeURIComponent(subj)}` : ''}`;
      const res = await api.get(url);
      if (res?.success) setQuizBoard(res.data || []);
    } catch {}
  }, []);

  const loadGames = useCallback(async (s) => {
    try {
      const res = await api.get(`/games/leaderboard?scope=${s}`);
      if (res?.success) setGamesBoard(res.data || []);
    } catch {}
  }, []);

  const loadSubjects = useCallback(async () => {
    try {
      const res = await api.get('/quiz/subjects');
      if (res?.success) setSubjects(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    setLoading(true);
    loadSubjects();
    Promise.allSettled([loadQuiz(scope, quizSubject), loadGames(scope)]).finally(() => setLoading(false));
  }, [scope, quizSubject, loadQuiz, loadGames, loadSubjects]);

  const board = tab === 'quiz' ? quizBoard : gamesBoard;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2" data-testid="leaderboard-title">
          <Trophy className="w-7 h-7 text-amber-500" /> Leaderboard
        </h1>
        <p className="text-slate-500 mt-0.5 text-sm">See where you rank among classmates and students across EDUVA</p>
      </div>

      {/* Tab + Scope */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        {/* Tabs */}
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
          <button
            onClick={() => setTab('quiz')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === 'quiz' ? 'bg-white text-[#2563eb] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-quiz-lb"
          >
            <HelpCircle className="w-3.5 h-3.5" /> Quiz
          </button>
          <button
            onClick={() => setTab('games')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === 'games' ? 'bg-white text-purple-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-games-lb"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Games
          </button>
        </div>

        {/* Scope */}
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
          {SCOPES.map(s => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${scope === s.key ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`scope-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quiz subject filter */}
      {tab === 'quiz' && subjects.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-400 font-medium">Filter:</span>
          <button
            onClick={() => setQuizSubject('')}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${quizSubject === '' ? 'bg-[#2563eb] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            All
          </button>
          {subjects.slice(0, 8).map(s => (
            <button
              key={s}
              onClick={() => setQuizSubject(s)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${quizSubject === s ? 'bg-[#2563eb] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Board */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-50 flex items-center justify-between">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
            {tab === 'quiz' ? 'Quiz' : 'Games'} Rankings — {SCOPES.find(s => s.key === scope)?.label}
          </p>
          <button onClick={() => { setLoading(true); Promise.allSettled([loadQuiz(scope, quizSubject), loadGames(scope)]).finally(() => setLoading(false)); }}
            className="text-slate-400 hover:text-slate-600">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-7 h-7 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : board.length === 0 ? (
          <div className="py-16 text-center">
            <Trophy className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No entries yet. Be the first!</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {board.map((entry, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 px-5 py-3.5 ${entry.is_me ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}
                data-testid={`lb-row-${i}`}
              >
                <div className="flex items-center justify-center w-8 flex-shrink-0">
                  <RankBadge rank={entry.rank} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${entry.is_me ? 'text-[#2563eb]' : 'text-slate-700'}`}>
                    {entry.student_name} {entry.is_me && <span className="text-xs text-blue-400">(You)</span>}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {entry.class_name && <span className="text-xs text-slate-400">{entry.class_name}</span>}
                    {scope === 'global' && entry.school_name && (
                      <span className="text-xs text-slate-400">· {entry.school_name}</span>
                    )}
                    {tab === 'quiz' && entry.total_attempts > 0 && (
                      <span className="text-xs text-slate-400">· {entry.total_attempts} attempts</span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {tab === 'quiz' ? (
                    <p className={`text-base font-black ${entry.best_pct >= 80 ? 'text-emerald-600' : entry.best_pct >= 60 ? 'text-blue-600' : 'text-amber-600'}`}>
                      {entry.best_pct}%
                    </p>
                  ) : (
                    <p className="text-base font-black text-purple-600">{(entry.best_score || 0).toLocaleString()}</p>
                  )}
                  <p className="text-[10px] text-slate-400">Best</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
